import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminTickets, adminRefundOrder } from '../../api/endpoints';
import TopBar from '../../components/TopBar';
import Splash from '../../components/Splash';
import { showConfirm, showAlert, hapticNotify } from '../../lib/tg';
import { formatDateTime } from '../../lib/format';

const STATUSES = ['ALL', 'PAID', 'PAID_PENDING', 'REFUNDED', 'CANCELED'] as const;

export default function TicketsList() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<typeof STATUSES[number]>('PAID');
  const { data, isLoading } = useQuery({
    queryKey: ['admin-orders'],
    queryFn: () => adminTickets({}),
  });

  const refundMut = useMutation({
    mutationFn: (orderId: number) => adminRefundOrder(orderId),
    onSuccess: () => { hapticNotify('success'); qc.invalidateQueries({ queryKey: ['admin-orders'] }); },
    onError: () => showAlert('Ошибка'),
  });

  async function onRefund(orderId: number) {
    if (await showConfirm('Отменить заказ и вернуть деньги? Действие необратимо.')) refundMut.mutate(orderId);
  }

  const filtered = useMemo(() => {
    const list = (data as any[]) ?? [];
    if (status === 'ALL') return list;
    return list.filter((o) => o.status === status);
  }, [data, status]);

  return (
    <div className="min-h-screen pb-8">
      <TopBar title="Заказы" />
      <div className="px-4 pt-3 mb-2 flex gap-2 overflow-x-auto no-scrollbar">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs ${status === s ? 'bg-accent text-white' : 'bg-bg-elev text-muted'}`}
          >
            {s}
          </button>
        ))}
      </div>

      {isLoading ? <Splash /> : (
        <div className="px-4 space-y-2">
          {filtered.map((o) => (
            <div key={o.id} className="card p-3">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-medium truncate">{o.session_title}</div>
                  <div className="text-xs text-muted">{o.cinema_name} · {formatDateTime(o.created_at)}</div>
                  <div className="text-xs text-muted">{o.phone}{o.username ? ` · @${o.username}` : ''}</div>
                  <div className="text-sm mt-1 space-y-0.5">
                    {o.seats.map((seat: any, i: number) => (
                      <div key={i}>{seat.sector} · ряд {seat.row}, место {seat.seat} — {seat.price.toLocaleString('ru-RU')} сум</div>
                    ))}
                  </div>
                  <div className="text-sm mt-1 font-medium">Итого: {o.total_amount.toLocaleString('ru-RU')} сум</div>
                </div>
                <div className="text-xs px-2 py-0.5 rounded-full bg-bg-elev shrink-0 ml-2">{o.status}</div>
              </div>
              {o.status === 'PAID' && (
                <button className="btn-secondary mt-2" onClick={() => onRefund(o.id)}>
                  Отменить и вернуть
                </button>
              )}
            </div>
          ))}
          {!filtered.length && <div className="text-muted text-center py-12">Пусто</div>}
        </div>
      )}
    </div>
  );
}
